import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Autocomplete,
  InputAdornment,
  Stack,
  IconButton,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  SwapHoriz as SwapHorizIcon,
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tesoreriaService, {
  GrupoCajaChica,
  MedioTransferencia,
  Banco,
  MarcaTarjeta,
  ChequeCartera,
} from '../../services/tesoreriaService';
import { formatCurrency } from '../../utils/formatters';
import api from '../../services/api';

interface Sucursal {
  nroSuc: number;
  nombreSucursal: string;
}

export interface TransferenciaItem {
  id?: number;
  nroFormulario?: number;
  nroTipoFormulario?: string;
  nroSuc?: number;
  fechaEmision?: string;
  codigoCaja?: number;
  usuario?: string;
  nombre?: string;
  descripcion?: string;
  importeTotal?: number;
  letra?: string;
  nroComprobante?: number;
  codigoCajaOrigen?: number;
  codigoCajaDestino?: number;
  mcpOrigen?: string;
  mcpDestino?: string;
  origen?: string;
  destino?: string;
  detalle?: string;
  monto?: number;
  importe?: number;
  medios?: Array<{ mcp: string; tipoPago: string; detalle: string; importe: number; nroCuenta: number; ingresoEgreso: string }>;
  [key: string]: any;
}

const TIPO_EFECTIVO = 'E';
const TIPO_CHEQUE = 'C';
const TIPO_TRANSFERENCIA = 'TB';
const TIPO_TARJETA = 'T';
const TIPO_OTROS = 'O';
const TIPO_RENDICION = 'R'; // Se trata igual que "Otros"

const TesoreriaTransferencias: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [grupos, setGrupos] = useState<GrupoCajaChica[]>([]);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState<GrupoCajaChica | null>(null);
  const [mcpsPorGrupo, setMcpsPorGrupo] = useState<MedioTransferencia[]>([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [loadingMcps, setLoadingMcps] = useState(false);

  const [chequeraOrigen, setChequeraOrigen] = useState<MedioTransferencia | null>(null);
  const [chequeraDestino, setChequeraDestino] = useState<MedioTransferencia | null>(null);
  const [mcpsDestinoPorTipo, setMcpsDestinoPorTipo] = useState<MedioTransferencia[]>([]);
  const [loadingMcpsDestino, setLoadingMcpsDestino] = useState(false);
  const [detalle, setDetalle] = useState('');
  const [monto, setMonto] = useState<string>('');

  const [bancos, setBancos] = useState<Banco[]>([]);
  const [marcasTarjeta, setMarcasTarjeta] = useState<MarcaTarjeta[]>([]);
  const [chequesCartera, setChequesCartera] = useState<ChequeCartera[]>([]);
  const [loadingChequesCartera, setLoadingChequesCartera] = useState(false);
  const [chequeSeleccionado, setChequeSeleccionado] = useState<ChequeCartera | null>(null);

  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [nroSucSeleccionado, setNroSucSeleccionado] = useState<number>(
    () => Number(localStorage.getItem('nroSuc') || localStorage.getItem('sucursal') || 0) || 0
  );

  const [transferencias, setTransferencias] = useState<TransferenciaItem[]>([]);
  const [transferenciasPendientes, setTransferenciasPendientes] = useState<{ payload: any; mcpOrigen: string; mcpDestino: string; detalle: string; importe: number }[]>([]);
  const [loadingListado, setLoadingListado] = useState(false);
  const [guardandoTodas, setGuardandoTodas] = useState(false);
  const [mostrarFormularioNueva, setMostrarFormularioNueva] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  });

  const [datosCheque, setDatosCheque] = useState({
    nroCheque: '',
    librador: '',
    nroLibrador: '',
    fechaEmision: new Date().toISOString().split('T')[0],
    fechaVencimiento: new Date().toISOString().split('T')[0],
    nroSucursal: '',
    concepto: '',
    ctaBancaria: '',
  });
  const [bancoSeleccionadoId, setBancoSeleccionadoId] = useState<number | ''>('');
  const [datosTarjeta, setDatosTarjeta] = useState({
    nroTarjeta: '',
    nroComprobanteTa: '',
    fecha: new Date().toISOString().split('T')[0],
    fechaPresentar: new Date().toISOString().split('T')[0],
    numeroroAutorizacion: '',
    telefono: '',
    titularTarjeta: '',
    codigoMarca: '' as number | '',
  });

  const tipoDestino = chequeraDestino?.tipo ? String(chequeraDestino.tipo).trim().toUpperCase() : '';
  const esEfectivo = tipoDestino === TIPO_EFECTIVO;
  const esCheque = tipoDestino === TIPO_CHEQUE;
  const esTarjeta =
    tipoDestino === TIPO_TARJETA ||
    (tipoDestino && tipoDestino.startsWith('T') && tipoDestino !== TIPO_TRANSFERENCIA);
  const esTransferenciaBancaria = tipoDestino === TIPO_TRANSFERENCIA;
  const esOtros = tipoDestino === TIPO_OTROS || tipoDestino === TIPO_RENDICION;

  // Tipo de la chequera origen (campo "tipo", comparación normalizada con trim)
  const tipoOrigen = (chequeraOrigen?.tipo ?? '').toString().trim().toUpperCase();

  // Opciones destino: MCPs de GET /api/Mcps?Tipo=X (todas las páginas), excluyendo origen
  const opcionesDestino = (() => {
    if (!chequeraOrigen || !tipoOrigen) return [];
    return mcpsDestinoPorTipo.filter(
      (m) => (m.mcp ?? '').trim() !== (chequeraOrigen?.mcp ?? '').trim()
    );
  })();

  const cargarGrupos = useCallback(async () => {
    setLoadingGrupos(true);
    try {
      const list = await tesoreriaService.getGruposCajaChica();
      setGrupos(list || []);
    } catch (error) {
      console.error('Error al cargar grupos:', error);
      enqueueSnackbar('Error al cargar los grupos de caja', { variant: 'error' });
      setGrupos([]);
    } finally {
      setLoadingGrupos(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    cargarGrupos();
  }, [cargarGrupos]);

  useEffect(() => {
    const cargarSucursales = async () => {
      setLoadingSucursales(true);
      try {
        const response = await api.get('/api/SucursalesVenta');
        const data = response.data;
        if (data && Array.isArray((data as any).items)) {
          setSucursales((data as any).items);
        } else if (Array.isArray(data)) {
          setSucursales(data);
        } else {
          setSucursales([]);
        }
      } catch (error: any) {
        console.error('Error al cargar sucursales:', error);
        enqueueSnackbar('Error al cargar sucursales', { variant: 'error' });
        setSucursales([]);
      } finally {
        setLoadingSucursales(false);
      }
    };
    cargarSucursales();
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (!grupoSeleccionado?.codigo) {
      setMcpsPorGrupo([]);
      setChequeraOrigen(null);
      setChequeraDestino(null);
      setMcpsDestinoPorTipo([]);
      return;
    }
    setLoadingMcps(true);
    setChequeraOrigen(null);
    setChequeraDestino(null);
    setMcpsDestinoPorTipo([]);
    tesoreriaService
      .getMcpsPorGrupo(grupoSeleccionado.codigo)
      .then((list) => setMcpsPorGrupo(list || []))
      .catch((err) => {
        console.error('Error al cargar MCPs:', err);
        enqueueSnackbar('Error al cargar medios de pago', { variant: 'error' });
        setMcpsPorGrupo([]);
      })
      .finally(() => setLoadingMcps(false));
  }, [grupoSeleccionado?.codigo, enqueueSnackbar]);

  useEffect(() => {
    if (!chequeraOrigen || !tipoOrigen) {
      setMcpsDestinoPorTipo([]);
      return;
    }
    setLoadingMcpsDestino(true);
    setMcpsDestinoPorTipo([]);
    tesoreriaService
      .getMcpsByTipo(tipoOrigen)
      .then((list) => setMcpsDestinoPorTipo(list || []))
      .catch((err) => {
        console.error('Error al cargar MCPs por tipo:', err);
        enqueueSnackbar('Error al cargar opciones de destino', { variant: 'error' });
        setMcpsDestinoPorTipo([]);
      })
      .finally(() => setLoadingMcpsDestino(false));
  }, [chequeraOrigen, tipoOrigen, enqueueSnackbar]);

  useEffect(() => {
    if (esCheque && bancos.length === 0) {
      tesoreriaService.getBancos().then(setBancos).catch(() => setBancos([]));
    }
    if (esTarjeta && marcasTarjeta.length === 0) {
      tesoreriaService.getMarcasTarjetas().then(setMarcasTarjeta).catch(() => setMarcasTarjeta([]));
    }
  }, [esCheque, esTarjeta, bancos.length, marcasTarjeta.length]);

  // Cargar cheques de cartera según el grupo/caja de origen cuando el destino es Cheque
  useEffect(() => {
    if (!grupoSeleccionado?.codigo || !esCheque) {
      setChequesCartera([]);
      setChequeSeleccionado(null);
      return;
    }

    const cargarCheques = async () => {
      setLoadingChequesCartera(true);
      try {
        const caja = await tesoreriaService.getCajaAbiertaPorGrupo(grupoSeleccionado.codigo);
        const codigoCaja = (caja as any)?.codigoCaja ?? (caja as any)?.nroCaja ?? (caja as any)?.codigo;
        if (!codigoCaja) {
          setChequesCartera([]);
          return;
        }
        const { cheques } = await tesoreriaService.getChequesCartera(codigoCaja);
        setChequesCartera(cheques || []);
      } catch (error) {
        console.error('Error al cargar cheques de cartera para transferencias:', error);
        enqueueSnackbar('Error al cargar cheques de cartera para la caja origen.', { variant: 'error' });
        setChequesCartera([]);
      } finally {
        setLoadingChequesCartera(false);
      }
    };

    cargarCheques();
  }, [grupoSeleccionado?.codigo, esCheque, enqueueSnackbar]);

  const fetchTransferencias = useCallback(
    async (
      pageNumber: number,
      pageSize: number,
      search: string,
      desde: string,
      hasta: string
    ) => {
      setLoadingListado(true);
      try {
        const result = await tesoreriaService.getTransferenciasCajaPaged({
          pageNumber: pageNumber + 1,
          pageSize,
          searchTerm: search || undefined,
          fechaDesde: desde || undefined,
          fechaHasta: hasta || undefined,
        });
        setTransferencias(result.items);
        setTotalCount(result.totalCount);
      } catch (error) {
        console.error('Error al cargar transferencias:', error);
        enqueueSnackbar('Error al cargar el listado de transferencias', { variant: 'error' });
        setTransferencias([]);
        setTotalCount(0);
      } finally {
        setLoadingListado(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    if (!mostrarFormularioNueva) {
      fetchTransferencias(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta);
    }
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, mostrarFormularioNueva, fetchTransferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const limpiarFormulario = () => {
    setDetalle('');
    setMonto('');
    setChequeraDestino(null);
    setDatosCheque({
      nroCheque: '',
      librador: '',
      nroLibrador: '',
      fechaEmision: new Date().toISOString().split('T')[0],
      fechaVencimiento: new Date().toISOString().split('T')[0],
      nroSucursal: '',
      concepto: '',
      ctaBancaria: '',
    });
    setBancoSeleccionadoId('');
    setDatosTarjeta({
      nroTarjeta: '',
      nroComprobanteTa: '',
      fecha: new Date().toISOString().split('T')[0],
      fechaPresentar: new Date().toISOString().split('T')[0],
      numeroroAutorizacion: '',
      telefono: '',
      titularTarjeta: '',
      codigoMarca: '',
    });
  };

  const handleAgregarTransferencia = () => {
    if (!grupoSeleccionado?.codigo) {
      enqueueSnackbar('Seleccione el Grupo de Caja', { variant: 'warning' });
      return;
    }
    if (!chequeraOrigen?.mcp) {
      enqueueSnackbar('Seleccione la Chequera de Origen', { variant: 'warning' });
      return;
    }
    if (!chequeraDestino?.mcp) {
      enqueueSnackbar('Seleccione la Chequera de Destino', { variant: 'warning' });
      return;
    }
    const montoNum = parseFloat(String(monto).replace(',', '.'));
    if (isNaN(montoNum) || montoNum <= 0) {
      enqueueSnackbar('Ingrese un monto válido', { variant: 'warning' });
      return;
    }

    // Validaciones específicas según el tipo de chequera destino,
    // copiando el comportamiento de "agregar pago" en movimientos varios.
    if (esCheque) {
      if (!chequeSeleccionado) {
        enqueueSnackbar('Debe seleccionar un cheque en cartera asociado a la caja origen.', {
          variant: 'warning',
        });
        return;
      }
    }

    if (esTarjeta) {
      const nroComp = Number(datosTarjeta.nroComprobanteTa);
      if (!datosTarjeta.codigoMarca || Number(datosTarjeta.codigoMarca) <= 0) {
        enqueueSnackbar('Debe seleccionar la Marca de tarjeta.', { variant: 'warning' });
        return;
      }
      if (!datosTarjeta.nroTarjeta || String(datosTarjeta.nroTarjeta).trim() === '') {
        enqueueSnackbar('Debe ingresar el N° de Tarjeta.', { variant: 'warning' });
        return;
      }
      if (!Number.isFinite(nroComp) || nroComp <= 0) {
        enqueueSnackbar('Debe ingresar un N° de Comprobante válido.', { variant: 'warning' });
        return;
      }
      if (!datosTarjeta.fecha) {
        enqueueSnackbar('Debe ingresar la Fecha de la operación.', { variant: 'warning' });
        return;
      }
      if (!datosTarjeta.numeroroAutorizacion || String(datosTarjeta.numeroroAutorizacion).trim() === '') {
        enqueueSnackbar('Debe ingresar el N° de Autorización/Lote.', { variant: 'warning' });
        return;
      }
    }

    const nroSucValue: number =
      (nroSucSeleccionado && nroSucSeleccionado > 0 ? nroSucSeleccionado : 0) ||
      Number(localStorage.getItem('nroSuc') || localStorage.getItem('sucursal') || 0) ||
      Number(chequeraOrigen.nroSuc || chequeraDestino.nroSuc || 0) ||
      0;

    if (!nroSucValue || nroSucValue <= 0) {
      enqueueSnackbar('Debe seleccionar un N° de Sucursal válido.', {
        variant: 'error',
      });
      return;
    }

    const medioCobroOrigen: any = {
      mcp: chequeraOrigen.mcp,
      importe: montoNum,
      detalle: detalle || '',
    };

    const medioCobroDestino: any = {
      mcp: chequeraDestino.mcp,
      importe: montoNum,
      detalle: detalle || '',
    };

    if (esCheque && chequeSeleccionado) {
      medioCobroDestino.cheque = {
        nroBanco: chequeSeleccionado.nroBanco ?? 0,
        nroSucursal: chequeSeleccionado.nroSucursal ?? 0,
        nroCheque: String(chequeSeleccionado.nroCheque ?? ''),
        nroLibrador: chequeSeleccionado.nroLibrador ?? '',
        librador: chequeSeleccionado.librador ?? '',
        fechaEmision: chequeSeleccionado.fechaEmision || new Date().toISOString(),
        fechaVencimiento: chequeSeleccionado.fechaVencimiento || new Date().toISOString(),
        ctaBancaria: chequeSeleccionado.nroCuenta ?? '',
        concepto: chequeSeleccionado.concepto ?? '',
        ...(chequeSeleccionado.nroCarga ? { nroCarga: chequeSeleccionado.nroCarga } : {}),
      };
    }
    if (esTarjeta) {
      medioCobroDestino.tarjeta = {
        codigoMarca: datosTarjeta.codigoMarca || 0,
        nroTarjeta: datosTarjeta.nroTarjeta || '',
        nroComprobanteTa: Number(datosTarjeta.nroComprobanteTa) || 0,
        fecha: datosTarjeta.fecha ? new Date(datosTarjeta.fecha).toISOString() : new Date().toISOString(),
        fechaPresentar: datosTarjeta.fechaPresentar
          ? new Date(datosTarjeta.fechaPresentar).toISOString()
          : new Date().toISOString(),
        numeroroAutorizacion: datosTarjeta.numeroroAutorizacion || '',
        telefono: datosTarjeta.telefono || '',
        titularTarjeta: datosTarjeta.titularTarjeta || '',
      };
    }

    const payload: any = {
      fecha: new Date().toISOString(),
      nroSuc: nroSucValue,
      descripcion: detalle || '',
      aclaracion: '',
      medioCobroOrigen,
      medioCobroDestino,
    };

    console.log('📤 Payload transferencia caja:', payload);

    setTransferenciasPendientes((prev) => [
      ...prev,
      {
        payload,
        mcpOrigen: (chequeraOrigen.mcp ?? '').trim(),
        mcpDestino: (chequeraDestino.mcp ?? '').trim(),
        detalle: detalle || '',
        importe: montoNum,
      },
    ]);
    limpiarFormulario();
    enqueueSnackbar('Transferencia agregada al listado', { variant: 'success' });
  };

  const handleQuitarPendiente = (idx: number) => {
    setTransferenciasPendientes((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleGuardarTodas = async () => {
    if (transferenciasPendientes.length === 0) {
      enqueueSnackbar('Agregue al menos una transferencia al listado', { variant: 'warning' });
      return;
    }
    setGuardandoTodas(true);
    let ok = 0;
    let err = 0;
    for (const item of transferenciasPendientes) {
      try {
        await tesoreriaService.postTransferenciaCaja(item.payload);
        ok += 1;
      } catch (error: any) {
        err += 1;
        console.error('❌ Error en postTransferenciaCaja:', error?.response?.data || error);
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.title ||
          error?.response?.data?.detail ||
          JSON.stringify(error?.response?.data) ||
          error?.message ||
          'Error';
        enqueueSnackbar(`Error en transferencia: ${msg}`, { variant: 'error' });
      }
    }
    setGuardandoTodas(false);
    if (ok > 0) {
      enqueueSnackbar(`${ok} transferencia(s) guardada(s) correctamente`, { variant: 'success' });
      setTransferenciasPendientes([]);
      setMostrarFormularioNueva(false);
      setPage(0);
    }
  };

  const handleCancelar = () => {
    setTransferenciasPendientes([]);
    setMostrarFormularioNueva(false);
    limpiarFormulario();
  };

  const handleAnular = async (_item: TransferenciaItem) => {
    enqueueSnackbar('Anulación pendiente de integrar con el backend', { variant: 'info' });
  };

  const getOrigenLabel = (t: TransferenciaItem) =>
    t.mcpOrigen ?? t.origen ?? t.medioCobroOrigen?.mcp ?? '-';
  const getDestinoLabel = (t: TransferenciaItem) =>
    t.mcpDestino ?? t.destino ?? t.medioCobroDestino?.mcp ?? '-';
  const getMonto = (t: TransferenciaItem) =>
    Number(
      t.monto ??
        t.importe ??
        t.importeTotal ??
        t.medioCobroDestino?.importe ??
        t.medioCobroOrigen?.importe ??
        0
    );

  const formatComprobante = (t: TransferenciaItem) => {
    const { letra, nroSuc, nroComprobante } = t;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }
    const formatPart = (value: number | string | undefined | null, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric.toString().padStart(length, '0');
      return String(value).padStart(length, '0');
    };
    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = (letra || '').trim().toUpperCase();
    return letraNormalizada ? `${letraNormalizada}-${sucursal}-${comprobante}` : '-';
  };

  const totalMonto = transferencias.reduce((sum, t) => sum + getMonto(t), 0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#1976d2',
                }}
              >
                <SwapHorizIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', mb: 0.5 }}>
                  {mostrarFormularioNueva ? 'Nueva transferencia' : 'Listado de transferencias'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                  {mostrarFormularioNueva
                    ? 'Transferencias entre medios de pago. Seleccione grupo, chequera origen, destino, detalle y monto.'
                    : 'Transferencias realizadas entre chequeras.'}
                </Typography>
              </Box>
            </Box>
            <Button
              variant={mostrarFormularioNueva ? 'outlined' : 'contained'}
              color="primary"
              startIcon={mostrarFormularioNueva ? <ArrowBackIcon /> : <AddIcon />}
              onClick={() => setMostrarFormularioNueva(!mostrarFormularioNueva)}
              sx={{ textTransform: 'none' }}
            >
              {mostrarFormularioNueva ? 'Volver al listado' : 'Nueva transferencia'}
            </Button>
          </Box>

          {!mostrarFormularioNueva ? (
            <>
              {/* Filtros - mismo estilo que Recibos */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Buscar por número, descripción, nombre"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPage(0);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fafafa',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          '&.Mui-focused': { bgcolor: '#ffffff' }
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#666' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Fecha Desde"
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => {
                        setFechaDesde(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fafafa',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          '&.Mui-focused': { bgcolor: '#ffffff' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Fecha Hasta"
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => {
                        setFechaHasta(e.target.value);
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#fafafa',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          '&.Mui-focused': { bgcolor: '#ffffff' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                  mb: 2,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Formulario</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Comprobante</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>MCP Origen</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>MCP Destino</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }} width={100}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingListado ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          Cargando...
                        </TableCell>
                      </TableRow>
                    ) : transferencias.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }} color="text.secondary">
                          No hay transferencias registradas.
                        </TableCell>
                      </TableRow>
                    ) : (
                      transferencias.map((t) => (
                        <TableRow key={t.id ?? t.nroFormulario ?? `${getOrigenLabel(t)}-${getDestinoLabel(t)}-${getMonto(t)}`} hover sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{t.nroFormulario ?? '-'}</TableCell>
                          <TableCell>{t.fechaEmision ? new Date(t.fechaEmision).toLocaleDateString() : '-'}</TableCell>
                          <TableCell>{formatComprobante(t)}</TableCell>
                          <TableCell>{getOrigenLabel(t)}</TableCell>
                          <TableCell>{getDestinoLabel(t)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>{formatCurrency(getMonto(t))}</TableCell>
                          <TableCell>{t.usuario ?? '-'}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="error" title="Anular" onClick={() => handleAnular(t)}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Filas por página"
                  sx={{ borderTop: '1px solid #e0e0e0' }}
                />
              </TableContainer>
              <Typography variant="body1" fontWeight={600}>
                Total: {formatCurrency(totalMonto)}
              </Typography>
            </>
          ) : (
          <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
              Nueva transferencia
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={grupos}
                  getOptionLabel={(option) => option.nombre || `Grupo ${option.codigo}`}
                  value={grupoSeleccionado}
                  onChange={(_, newValue) => setGrupoSeleccionado(newValue)}
                  loading={loadingGrupos}
                  renderInput={(params) => (
                    <TextField {...params} label="Grupo de Caja" placeholder="Seleccionar grupo..." />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={mcpsPorGrupo}
                  getOptionLabel={(option) => `${option.nombre || ''} (${option.mcp || ''})`}
                  value={chequeraOrigen}
                  onChange={(_, newValue) => {
                    setChequeraOrigen(newValue);
                    setChequeraDestino(null);
                  }}
                  loading={loadingMcps}
                  disabled={!grupoSeleccionado}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Chequera Origen"
                      placeholder="Buscar..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: '#666' }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id ?? option.mcp}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <AccountBalanceIcon sx={{ color: '#1976d2' }} />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {option.nombre || option.mcp || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.mcp} {option.tipo ? ` · Tipo: ${option.tipo}` : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={opcionesDestino}
                  getOptionLabel={(option) =>
                    option
                      ? `${option.nombre || option.descripcion || option.mcp || ''} (${option.mcp || ''})`.trim() || 'Sin nombre'
                      : ''
                  }
                  filterOptions={(options) => options}
                  value={chequeraDestino}
                  onChange={(_, newValue) => setChequeraDestino(newValue)}
                  loading={loadingMcpsDestino}
                  disabled={!chequeraOrigen}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Chequera Destino"
                      placeholder="Buscar..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: '#666' }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id ?? option.mcp}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <AccountBalanceIcon sx={{ color: '#1976d2' }} />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {option.nombre || option.mcp || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.mcp} {option.tipo ? ` · Tipo: ${option.tipo}` : ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel id="transferencias-nro-suc-label">Nº Sucursal</InputLabel>
                  <Select
                    labelId="transferencias-nro-suc-label"
                    value={nroSucSeleccionado > 0 ? nroSucSeleccionado : ''}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setNroSucSeleccionado(value);
                      if (value > 0) {
                        try {
                          localStorage.setItem('nroSuc', String(value));
                          localStorage.setItem('sucursal', String(value));
                        } catch {}
                      }
                    }}
                    label="Nº Sucursal"
                    disabled={loadingSucursales}
                  >
                    <MenuItem value="">
                      <em>Seleccionar sucursal</em>
                    </MenuItem>
                    {sucursales.map((suc) => (
                      <MenuItem key={suc.nroSuc} value={suc.nroSuc}>
                        {suc.nroSuc} - {suc.nombreSucursal?.trim() || `Sucursal ${suc.nroSuc}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Detalle"
                  value={detalle}
                  onChange={(e) => setDetalle(e.target.value)}
                  placeholder="Descripción de la transferencia"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Monto"
                  value={monto}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9,.]/g, '').replace(',', '.');
                    if (v === '' || /^\d*\.?\d*$/.test(v)) setMonto(v === '' ? '' : v);
                  }}
                  placeholder="0.00"
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAgregarTransferencia}
                  fullWidth
                  sx={{ textTransform: 'none' }}
                >
                  Agregar
                </Button>
              </Grid>
            </Grid>

            {chequeraDestino && !esEfectivo && (
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Datos del medio de pago (destino: {chequeraDestino.nombre || chequeraDestino.mcp})
                </Typography>
                {esCheque && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={chequesCartera}
                        loading={loadingChequesCartera}
                        value={chequeSeleccionado}
                        onChange={(_, value) => {
                          setChequeSeleccionado(value);
                          if (value) {
                            const fechaEmision = value.fechaEmision
                              ? new Date(value.fechaEmision).toISOString().split('T')[0]
                              : new Date().toISOString().split('T')[0];
                            const fechaVencimiento = value.fechaVencimiento
                              ? new Date(value.fechaVencimiento).toISOString().split('T')[0]
                              : new Date().toISOString().split('T')[0];
                            setDatosCheque({
                              nroCheque: String(value.nroCheque ?? ''),
                              librador: value.librador ?? '',
                              nroLibrador: value.nroLibrador ?? '',
                              fechaEmision,
                              fechaVencimiento,
                              nroSucursal: String(value.nroSucursal ?? ''),
                              concepto: value.concepto ?? '',
                              ctaBancaria: value.nroCuenta ?? '',
                            });
                          } else {
                            setDatosCheque({
                              nroCheque: '',
                              librador: '',
                              nroLibrador: '',
                              fechaEmision: new Date().toISOString().split('T')[0],
                              fechaVencimiento: new Date().toISOString().split('T')[0],
                              nroSucursal: '',
                              concepto: '',
                              ctaBancaria: '',
                            });
                          }
                        }}
                        getOptionLabel={(option) =>
                          option
                            ? `Cheque ${option.nroCheque ?? ''} · ${option.librador ?? ''} · ${formatCurrency(
                                option.importe ?? 0
                              )}`
                            : ''
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Cheque en cartera"
                            placeholder="Seleccione un cheque ya cargado en la caja origen"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Banco</InputLabel>
                        <Select
                          value={bancoSeleccionadoId}
                          onChange={(e) => setBancoSeleccionadoId(Number(e.target.value) || '')}
                          label="Banco"
                        >
                          {bancos.map((b) => (
                            <MenuItem key={b.id} value={b.id}>{b.nombre}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="N° Sucursal"
                        value={datosCheque.nroSucursal}
                        onChange={(e) => setDatosCheque((prev) => ({ ...prev, nroSucursal: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="N° Cheque"
                        value={datosCheque.nroCheque}
                        onChange={(e) => setDatosCheque((prev) => ({ ...prev, nroCheque: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Librador"
                        value={datosCheque.librador}
                        onChange={(e) => setDatosCheque((prev) => ({ ...prev, librador: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Fecha Emisión"
                        type="date"
                        value={datosCheque.fechaEmision}
                        onChange={(e) => setDatosCheque((prev) => ({ ...prev, fechaEmision: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Fecha Vencimiento"
                        type="date"
                        value={datosCheque.fechaVencimiento}
                        onChange={(e) => setDatosCheque((prev) => ({ ...prev, fechaVencimiento: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Concepto"
                        value={datosCheque.concepto}
                        onChange={(e) => setDatosCheque((prev) => ({ ...prev, concepto: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Cta. Bancaria"
                        value={datosCheque.ctaBancaria}
                        onChange={(e) => setDatosCheque((prev) => ({ ...prev, ctaBancaria: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                )}
                {esTarjeta && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Marca Tarjeta</InputLabel>
                        <Select
                          value={datosTarjeta.codigoMarca}
                          onChange={(e) => setDatosTarjeta((prev) => ({ ...prev, codigoMarca: Number(e.target.value) || '' }))}
                          label="Marca Tarjeta"
                        >
                          {marcasTarjeta.map((m) => (
                            <MenuItem key={m.id} value={m.id}>{m.nombreMarca || m.nombre}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="N° Tarjeta"
                        value={datosTarjeta.nroTarjeta}
                        onChange={(e) => setDatosTarjeta((prev) => ({ ...prev, nroTarjeta: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="N° Comprobante"
                        value={datosTarjeta.nroComprobanteTa}
                        onChange={(e) => setDatosTarjeta((prev) => ({ ...prev, nroComprobanteTa: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Titular"
                        value={datosTarjeta.titularTarjeta}
                        onChange={(e) => setDatosTarjeta((prev) => ({ ...prev, titularTarjeta: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Fecha"
                        type="date"
                        value={datosTarjeta.fecha}
                        onChange={(e) => setDatosTarjeta((prev) => ({ ...prev, fecha: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Fecha Presentar"
                        type="date"
                        value={datosTarjeta.fechaPresentar}
                        onChange={(e) => setDatosTarjeta((prev) => ({ ...prev, fechaPresentar: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="N° Autorización"
                        value={datosTarjeta.numeroroAutorizacion}
                        onChange={(e) => setDatosTarjeta((prev) => ({ ...prev, numeroroAutorizacion: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Teléfono"
                        value={datosTarjeta.telefono}
                        onChange={(e) => setDatosTarjeta((prev) => ({ ...prev, telefono: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                )}
                {(esTransferenciaBancaria || esOtros) && (
                  <Typography variant="body2" color="text.secondary">
                    Los datos del medio ya están cargados según la chequera de destino seleccionada. Complete detalle y monto arriba.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 2, color: '#333' }}>
            Transferencias a realizar
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Origen</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Destino</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Detalle</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }} width={80}>Quitar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transferenciasPendientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }} color="text.secondary">
                      Agregue transferencias con el botón de arriba
                    </TableCell>
                  </TableRow>
                ) : (
                  transferenciasPendientes.map((item, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{item.mcpOrigen || '-'}</TableCell>
                      <TableCell>{item.mcpDestino || '-'}</TableCell>
                      <TableCell>{item.detalle || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(item.importe)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" title="Quitar" onClick={() => handleQuitarPendiente(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
            Total: {formatCurrency(transferenciasPendientes.reduce((s, t) => s + t.importe, 0))}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancelar} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckIcon />}
              onClick={handleGuardarTodas}
              disabled={transferenciasPendientes.length === 0 || guardandoTodas}
              sx={{ textTransform: 'none' }}
            >
              {guardandoTodas ? 'Guardando...' : 'Guardar todas'}
            </Button>
          </Stack>
          </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TesoreriaTransferencias;
